import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import truncate from "lodash/truncate";
import { PageLayout } from "@components";
import { Tabs, Tab } from "@rentablo/components";
import { useInvestmentsId } from "@rentablo/api/apiv1";
import { InvestmentEditQuoteData } from "./components/InvestmentEditQuoteData";
import { InvestmentEditAdvancedTab } from "./components/InvestmentEditAdvancedTab";
import { InvestmentEditBasicTab } from "./components/InvestmentEditBasicTab";

export interface Props {
  investmentId: number;
}

export const InvestmentEditPage = ({ investmentId }: Props) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: investment,
    isFetching,
    isPending,
  } = useInvestmentsId(
    { id: investmentId! },
    { enabled: investmentId !== undefined }
  );

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const defaultTabKey = searchParams.get("tab");

  const breadcrumbs: BreadcrumbItem[] = [
    {
      to: "/",
      text: t("breadcrumbs.home"),
    },
    {
      to: "/investments",
      text: t("breadcrumbs.investments"),
    },
    {
      to: `/investments/${investmentId}`,
      text: investment?.hasValidIsin
        ? investment?.isin
        : truncate(investment?.name),
    },
    {
      text: t("investmentEditPage.breadcrumbs.edit"),
    },
  ];

  return (
    <PageLayout
      isFetching={isFetching}
      breadcrumbs={breadcrumbs}
      title={investment?.name ?? investment?.isin}
    >
      {!!investment && !isPending ? (
        <Tabs onChange={handleTabChange} defaultKey={defaultTabKey}>
          <Tab label={t("investmentEditPage.tabs.basic")} key="basic">
            <InvestmentEditBasicTab investment={investment} />
          </Tab>
          <Tab label={t("investmentEditPage.tabs.quoteData")} key="quoteData">
            <InvestmentEditQuoteData investment={investment} />
          </Tab>
          <Tab label={t("investmentEditPage.tabs.advanced")} key="advanced">
            <InvestmentEditAdvancedTab investment={investment} />
          </Tab>
        </Tabs>
      ) : (
        <Typography>{t("investmentEditPage.noInvestment")}</Typography>
      )}
    </PageLayout>
  );
};
